import React, { useState } from "react";
import { Mail, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { BASE_URL, RESET_PASSWORD_URL, trim } from "../../../utils/helpers";
import { Link, useNavigate } from "react-router-dom";

type FormErrors = {
  email?: string;
  server?: string;
};

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState<string>("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const navigate = useNavigate();

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};

    const trimmedEmail = trim(email);
    if (!trimmedEmail) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = trim(email);
    setEmail(trimmedEmail);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BASE_URL}forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: trimmedEmail,
          reset_url: RESET_PASSWORD_URL,
        }),
      });

      if (response?.status === 200) {
        const data = await response.json();

        setIsSuccess(true);

        setTimeout(() => {
          navigate("/reset-password", {
            state: { email: trimmedEmail },
          });
        }, 2000);
      } else {
        const data = await response.json();
        setErrors({
          server:
            data?.message || "Email not found. Please check and try again.",
        });
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setErrors({
        server: "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl p-6 lg:p-8 border shadow-sm">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Check your email
            </h2>
            <p className="text-gray-600">
              We've sent password reset instructions to{" "}
              <span className="font-medium text-gray-900">{email}</span>
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to reset password...
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 lg:p-8 border shadow-sm">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center space-y-1">
            <h2 className="text-2xl font-bold text-gray-900">
              Forgot password?
            </h2>
            <p className="text-gray-600 text-sm">
              Enter your email and we'll send you reset instructions
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      email: undefined,
                      server: undefined,
                    }));
                  }}
                  placeholder="you@company.com"
                  className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-500 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  autoFocus
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Server error */}
            {errors.server && (
              <p className="text-sm text-red-600 text-center">
                {errors.server}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-500 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Sending instructions…</span>
                </>
              ) : (
                <>
                  <span>Send reset instructions</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          {/* Back to login */}
          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForgotPasswordForm;
